import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Play } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { embedUrl, youtubeThumb } from "@/lib/media";
import { PageHero } from "@/components/PageHero";
import { MediaDialog } from "@/components/MediaView";
import { useLang } from "@/context/LangContext";
import { useSite } from "@/context/SiteContext";
import { PageLayout } from "@/components/PageLayout";

export const Thumb = ({ item }) => {
  if (item.kind === "photo") return <img src={fileUrl(item.url)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />;
  const yt = youtubeThumb(item.url);
  if (yt) return <img src={yt} alt="" loading="lazy" className="h-full w-full object-cover" />;
  if (embedUrl(item.url)) return <div className="h-full w-full bg-gradient-to-br from-[#1C2D42] to-[#0B111E]" />;
  return <video src={`${fileUrl(item.url)}#t=0.5`} preload="metadata" muted playsInline className="h-full w-full object-cover" />;
};

const Tile = ({ item, i, onOpen, title }) => (
  <motion.button
    data-testid={`gallery-item-${item.id}`}
    onClick={onOpen}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
    className="group relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#131C2E] text-left"
  >
    <Thumb item={item} />
    {item.kind === "video" && (
      <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[#D4AF37] text-[#0B111E] shadow-lg transition-transform group-hover:scale-110">
        <Play className="h-5 w-5 translate-x-[1px]" />
      </span>
    )}
    {(title || item.album) && (
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-3 pt-12">
        {title && <span data-ve-rec={`gallery|${item.id}|title_`} className="block truncate font-ui text-sm font-semibold text-white">{title}</span>}
        {item.album && <span className="block truncate font-mono text-[11px] uppercase tracking-[0.2em] text-[#F3D27A]">{item.album}</span>}
      </span>
    )}
  </motion.button>
);

const useGallery = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/gallery").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  return items;
};

// Album filter chips + photo/video grid + full-size viewer.
const MediaGrid = ({ items }) => {
  const { t, pick } = useLang();
  const [album, setAlbum] = useState("all");
  const [open, setOpen] = useState(null);
  const albums = useMemo(() => [...new Set(items.map((x) => x.album).filter(Boolean))], [items]);
  const shown = album === "all" ? items : items.filter((x) => x.album === album);
  const chip = (on) => `shrink-0 rounded-full px-4 py-2 font-ui text-sm transition-colors duration-300 ${on ? "bg-[#D4AF37] font-semibold text-[#0B111E]" : "text-[#F4EEE2]/80 hover:bg-[#F4EEE2]/5"}`;
  return (
    <>
      {albums.length > 0 && (
        <div data-testid="events-album-filters" className="mb-8 flex gap-2 overflow-x-auto pb-1">
          <button data-testid="events-album-all" onClick={() => setAlbum("all")} className={chip(album === "all")}>{t("cat.all")}</button>
          {albums.map((a, i) => <button key={a} data-testid={`events-album-${i}`} onClick={() => setAlbum(a)} className={chip(album === a)}>{a}</button>)}
        </div>
      )}
      {shown.length ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((x, i) => <Tile key={x.id} item={x} i={i} title={pick(x.title_en, x.title_mh)} onOpen={() => setOpen(x)} />)}
        </div>
      ) : (
        <p data-testid="events-empty" className="py-20 text-center font-display text-3xl italic text-[#B7BFCC]">{t("events.empty")}</p>
      )}
      <MediaDialog item={open} onClose={() => setOpen(null)} title={open ? pick(open.title_en, open.title_mh) : ""} caption={[open?.album, open?.date].filter(Boolean).join(" · ")} />
    </>
  );
};

const coverOf = (d, items) => {
  if (d.cover_url) return fileUrl(d.cover_url);
  const photo = items.find((x) => x.kind === "photo");
  return photo ? fileUrl(photo.url) : items.map((x) => youtubeThumb(x.url)).find(Boolean) || "";
};

const DeptCard = ({ d, items, i }) => {
  const { t, pick } = useLang();
  const cover = coverOf(d, items);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}>
      <Link to={`/events/${d.slug}`} data-testid={`events-dept-card-${d.slug}`} className="group relative block aspect-[4/3] overflow-hidden rounded-[28px] bg-[#131C2E]">
        {cover ? <img src={cover} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" /> : <div className="absolute inset-0 bg-gradient-to-br from-[#1C2D42] to-[#0B111E]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
        <span className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-[#0B111E] transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
        <div className="absolute inset-x-0 bottom-0 p-6">
          <p data-ve-rec={`departments|${d.slug}|@`} className="font-display text-3xl font-semibold leading-tight text-white">{pick(d.en, d.mh)}</p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F3D27A]">{items.length} {t("events.items")}</p>
        </div>
      </Link>
    </motion.div>
  );
};

export function Department() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const { settings } = useSite();
  const items = useGallery();
  const deps = Array.isArray(settings?.departments) ? settings.departments : null;
  const d = deps?.find((x) => x.slug === slug);
  if (deps && !d) return <Navigate to="/events" replace />;
  if (!d) return <main className="min-h-screen" />;
  return (
    <main data-testid={`dept-page-${slug}`}>
      <PageLayout key={slug} page={`dept:${slug}`} sections={{
        hero: <PageHero key={slug} testid="dept-hero" over={t("nav.gallery")} title={pick(d.en, d.mh)} lead={t("events.deptLead")} titleRec={`departments|${d.slug}|@`} />,
        media: (
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <Link to="/events" data-testid="dept-back-link" className="mb-8 inline-flex items-center gap-2 font-ui text-sm text-[#B7BFCC] hover:text-[#D4AF37]"><ArrowLeft className="h-4 w-4" /> {t("events.all")}</Link>
        {items && <MediaGrid key={slug} items={items.filter((x) => x.department === slug)} />}
      </section>
        ),
      }} />
    </main>
  );
}

export default function Events() {
  const { t } = useLang();
  const { settings } = useSite();
  const items = useGallery() || [];
  const deps = Array.isArray(settings?.departments) ? settings.departments : [];
  const slugs = new Set(deps.map((d) => d.slug));
  const other = items.filter((x) => !slugs.has(x.department));
  return (
    <main data-testid="events-page">
      <PageLayout page="events" sections={{
        hero: <PageHero testid="events-hero" over={t("events.over")} title={t("events.title")} lead={t("events.lead")} />,
        departments: (
      <section className="mx-auto max-w-[1400px] px-5 pb-12 md:px-10">
        <div data-testid="events-dept-grid" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {deps.map((d, i) => <DeptCard key={d.slug} d={d} i={i} items={items.filter((x) => x.department === d.slug)} />)}
        </div>
      </section>
        ),
        other: other.length > 0 && (
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
          <div className="mt-8" data-testid="events-other">
            <h2 className="font-display mb-8 text-4xl font-semibold text-[#F4EEE2]">{t("events.more")}</h2>
            <MediaGrid items={other} />
          </div>
      </section>
        ),
      }} />
    </main>
  );
}
